import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { useAuth, type SessionUser } from '@/context/AuthContext';
import { api } from '@/utils/api';
import PasswordInput from '@/components/base/PasswordInput';

/**
 * Set a new password (LoginController::changePassword): at least 8 characters,
 * typed twice. Required after signing in with a temporary password; also open
 * from the account menu at any time.
 */
export default function ChangePassword() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const forced = Boolean(user?.mustChangePassword);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const valid = password.length >= 8 && confirmation === password;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setError('');
    setSaving(true);
    try {
      const updated = await api<SessionUser>('/password/change', {
        method: 'POST',
        body: JSON.stringify({ password, passwordConfirmation: confirmation }),
      });
      updateUser(updated);
      navigate('/dashboard', { replace: true, state: { passwordChanged: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the password.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'mt-1 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <span className="page-rule mx-auto" aria-hidden="true" />
          <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
            {forced ? 'Set a new password' : 'Change your password'}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">
            {forced
              ? `Welcome${user ? `, ${user.name.split(' ')[0]}` : ''}. Replace your temporary password to continue.`
              : 'Choose a new password for your agency account.'}
          </p>
        </div>

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="rounded-lg border border-background-200 bg-background-50 p-6"
        >
          {error ? (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800"
            >
              <i className="ri-error-warning-line mt-0.5" />
              {error}
            </div>
          ) : null}

          <label className="block text-sm font-medium text-foreground-800">
            New password
            <PasswordInput
              autoComplete="new-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </label>
          <p className={`mt-1 text-[11px] ${tooShort ? 'text-primary-700' : 'text-foreground-500'}`}>
            At least 8 characters.
          </p>

          <label className="mt-4 block text-sm font-medium text-foreground-800">
            Confirm password
            <PasswordInput
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className={inputClass}
            />
          </label>
          {mismatch ? <p className="mt-1 text-[11px] text-primary-700">The passwords don&apos;t match.</p> : null}

          <Button type="submit" size="lg" className="mt-6 w-full" disabled={!valid || saving}>
            {saving ? 'Saving…' : 'Save new password'}
          </Button>

          <div className="mt-4 text-center">
            {forced ? (
              <button
                type="button"
                onClick={logout}
                className="cursor-pointer text-xs font-medium text-foreground-500 hover:text-foreground-800"
              >
                Sign out
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="cursor-pointer text-xs font-medium text-foreground-500 hover:text-foreground-800"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
